/**
 * Avisos del sistema (Fase 6, 0.12.0).
 *
 * Lo que de verdad se puede en un iPhone, comprobado antes de implementarlo:
 *
 * - **No existen notificaciones locales programadas.** Safari solo entrega
 *   notificaciones que llegan por Web Push, y eso necesita un servidor que
 *   las mande en el instante exacto. No hay API para decir "avísame en 90
 *   segundos" (la de Notification Triggers nunca llegó a Safari).
 * - **Con la app suspendida, nada corre.** iOS detiene los temporizadores en
 *   cuanto DuoFit deja de estar a la vista, así que un aviso en JavaScript no
 *   se dispara solo.
 * - Lo que sí funciona sin servidor: mientras la app siga **despierta**, la
 *   notificación del sistema aparece igual que la de cualquier app. Y la app
 *   se puede mantener despierta con la pantalla bloqueada reproduciendo audio
 *   de verdad (`sonido.ts`), que es como lo hacen los temporizadores web.
 *
 * Por eso el aviso del descanso va en dos partes: el audio mantiene viva la
 * app y hace sonar los tonos, y esta notificación pone el banner en la
 * pantalla bloqueada. Si la app no está despierta, no hay aviso: la pantalla
 * lo dice en lugar de prometerlo.
 */

export type EstadoPermiso = 'sin_soporte' | 'sin_pedir' | 'concedido' | 'negado';

/** ¿Este navegador puede mostrar notificaciones desde el Service Worker? */
export function notificacionesSoportadas(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'showNotification' in ServiceWorkerRegistration.prototype
  );
}

export function permisoDeAvisos(): EstadoPermiso {
  if (!notificacionesSoportadas()) return 'sin_soporte';
  switch (Notification.permission) {
    case 'granted':
      return 'concedido';
    case 'denied':
      return 'negado';
    default:
      return 'sin_pedir';
  }
}

/**
 * Pide el permiso. Hay que llamarlo dentro de un toque: Safari no lo concede
 * de otra forma. Devuelve el estado que quedó.
 */
export async function pedirPermisoDeAvisos(): Promise<EstadoPermiso> {
  if (!notificacionesSoportadas()) return 'sin_soporte';
  try {
    await Notification.requestPermission();
  } catch {
    // Safari antiguo con la forma de callback: el estado se lee abajo
  }
  return permisoDeAvisos();
}

/** Para no repetir el banner del mismo descanso si la pantalla se redibuja. */
let avisado: string | null = null;

/**
 * El banner de "Terminó el descanso". `clave` es la hora de fin del descanso,
 * así que cada descanso avisa una sola vez. Sin permiso no hace nada.
 */
export async function avisarFinDeDescanso(clave: string, texto: string): Promise<void> {
  if (avisado === clave) return;
  avisado = clave;
  if (permisoDeAvisos() !== 'concedido') return;
  try {
    const registro = await navigator.serviceWorker.ready;
    await registro.showNotification('Terminó el descanso', {
      body: texto,
      tag: 'descanso',
      // Vuelve a sonar y a vibrar si llega otra: el aviso importa en el gym
      renotify: true,
      // Los nombres reales de los íconos (vite.config.ts) y relativos, para
      // que funcionen con cualquier `base`. Antes decían "icono-192.png",
      // que no existe, y el iPhone ponía su ícono genérico.
      icon: 'icon-192.png',
      badge: 'icon-192.png',
      data: { ruta: '#/gym/entrenamiento' },
    } as NotificationOptions);
  } catch {
    // Sin Service Worker listo no hay banner; el sonido y la pantalla siguen
  }
}

/** Al empezar o cambiar un descanso: el siguiente fin vuelve a poder avisar. */
export function olvidarAvisoDeDescanso(): void {
  avisado = null;
}
